import {Grant} from '@meprism/shared/src/redux/product/ProductTypes'
import Cookies from 'universal-cookie'
export enum Environments {
  local = 'local',
  sandbox = 'sandbox',
  development = 'development',
  staging = 'staging',
  production = 'production',
}
export type LogLevels = 'DEBUG' | 'INFO' | 'ERROR'
export const currentEnvironment = (): Environments => {
  if (process.env.REACT_APP_LOCAL) {
    return Environments.local
  } else if ((process.env.REACT_APP_API_FQDN as string).includes('sandbox')) {
    return Environments.sandbox
  } else if (
    (process.env.REACT_APP_API_FQDN as string).includes('development')
  ) {
    return Environments.development
  } else if ((process.env.REACT_APP_API_FQDN as string).includes('staging')) {
    return Environments.staging
  }

  return Environments.production
}

export const logLevel = (loggerLevel: LogLevels): LogLevels => {
  if (currentEnvironment() === Environments.local) {
    return 'DEBUG'
  } else {
    return loggerLevel
  }
}

export const bytesToGbConverter = (size: number): number => {
  return size / (1000 * 1000 * 1000)
}

export const compositeCMSString = (
  cmsString: string,
  replacement: string | string[],
  replacing = '{0}',
): string => {
  try {
    if (Array.isArray(replacement)) {
      let replacedString = cmsString
      replacement.forEach((replace, index) => {
        replacedString = replacedString.replace(`{${index}}`, replace)
      })
      return replacedString
    } else {
      return cmsString.replace(replacing, replacement)
    }
  } catch (error) {
    // fail silently. don't worry it's safe
    return ''
  }
}

export const isObjectEmpty = (obj: any): boolean =>
  obj && Object.keys(obj).length === 0

export const isValidDate = (d: any): boolean => {
  return d instanceof Date && !isNaN(d.getTime())
}

export const formatDate = (date: string): string => {
  const newDate = new Date(date)

  return isValidDate(newDate)
    ? `${newDate.getMonth() + 1}/${newDate.getDate()}/${newDate.getFullYear()}`
    : ''
}

// zips 2 arrays in order - if the lengths are different, returns an array of the larger length with missing elements
// returned as undefined
export const zip = <T, U>(a1: T[], a2: U[]): [T?, U?][] => {
  if (a2.length > a1.length) {
    return a2.map((a, i) => [a1[i], a])
  } else {
    return a1.map((a, i) => [a, a2[i]])
  }
}

// from https://www.typescriptlang.org/docs/handbook/unions-and-intersections.html
export const assertNever = (x: never): never => {
  throw new Error('Unexpected object: ' + x)
}

export const nodeToString = (node: any): string => {
  if (typeof node === 'string') return node;

  if (node?.props?.children) {
    if (typeof node.props.children === 'string') return node.props.children;
    if (Array.isArray(node.props.children)) return node.props.children.map(nodeToString).join(' ');
    return String(node.props.children ?? '');
  }
  return '';
}

export const getCol4DisplayValue = (status: string): string => {
  if (status === 'not_found') return 'Not Found';
  if (['optout_in_progress', 'new'].includes(status)) return 'Removing';
  return 'Removed';
}

export const decodeString=(encodedStr: string)=>{
    const percentDecoded = decodeURIComponent(encodedStr);
    const jsonStr = atob(percentDecoded);
    const data = JSON.parse(jsonStr);
    return data;
}
export const validateEmail = (email: string) => {
  if (!email) {
    return false
  }
  // this beautiful construction is a classic from
  // https://stackoverflow.com/questions/201323/how-can-i-validate-an-email-address-using-a-regular-expression
  return !!email.toLowerCase().match(
    // eslint-disable-next-line no-control-regex
    /(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9]))\.){3}(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9])|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/,
  )
}

export const isAddressIncomplete = (address: any, grant?: Grant) => {
  const requiredFields = ['city', 'state', 'zip']
  if (grant) requiredFields.push('streetAddress')
  return requiredFields.some(field => !address[field]?.trim())
}

export const formatPhoneNumbers = (
  phones: { value: string }[] = []
): { value: string }[] => {
  return phones.map((p) => ({
    value: `+1${p.value.replace(/[^\d]/g, '')}`,
  }));
};

export const isValidEmail = (email: string) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[a-z]{2,}$/;
  return emailRegex.test(email.trim());
};

export const formatPhoneNumberDisplay = (value?: string) => {
  if (!value) return '';

  const digits = value.replace(/\D/g, '');

  if (digits.length <= 3) return digits;
  if (digits.length <= 6)
    return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;

  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
};

export const setFamilyPromptCookie=()=>{
  const cookie = new Cookies()
  const familyPlanCookie = cookie.get(
    'PromptForFamilyPlan'
  )
  if(!familyPlanCookie){
    cookie.set('PromptForFamilyPlan', 'true', {
      path: '/',
      maxAge: 60 * 60 * 24 * 365 * 100,
    })
  }
}

export function normalizeCity(value: string): string {
  if (!value) return "";

  let normalized = value.replace(/_/g, " ").trim();
  normalized = normalized
    .toLowerCase()
    .split(" ")
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
  return normalized;
}

export const normalizeMiddleName = (value: string): string => {
  if (!value) return "";

  if (value.toLowerCase() === "nmn") {
    return "NMN";
  }
  return value;
};
